import {Component, Input, OnDestroy} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {setShareCopyStatus, ShareCopyStatus} from '../share/share.actions';
import {IAppState} from '../app.state';
import {ShareCopyService} from '../share';
import {closeSelectDialog, openSelectDialog} from '../visual/visual.actions';
import {
  getAllAvailableOrganizations,
  getCurrentOrgWorkspaces,
  getIsSelectReady,
  getSelectedWorkspace
} from '../select/select.selector';
import {getSelectedOrganization} from '../organizations/organizations.selector';
import {selectCurrentProfile} from '../profile/profile.selector';
import {ProfileService} from '../profile/profile.service';
import {NIM_AUTH_TYPE} from '../auth-form/auth.model';
import {Config} from "../universal-config";
import {NoteService} from "../share/note/note.service";
import {combineLatest, Subscription} from "rxjs";
import {filter, take} from "rxjs/operators";

@Component({
  selector: 'nns-save-action',
  template: `
    <div *ngIf="profile">
      <nns-save-button class="nns-animated"
        [ngClass]="{'primary': isPrimary, 'disabled': !visual.isSocketConnected}"
        (click)="openSelectDialog()"
        [state]="state"
        [showTitle]="showTitle"
        [type]="note.type"
        title="Save to FuseBase"
      ></nns-save-button>

      <div class="nns-save-button-dropdown" *ngIf="login?.isLoginDialogOpened">
        <div class="nns-save-button-dropdown-inner">
          <div class="nns-save-button-dropdown-holder">
            <nns-login-dialog [visual]="visual"></nns-login-dialog>
          </div>
        </div>
      </div>

      <div class="nns-save-button-dropdown" *ngIf="visual?.isSelectDialogOpened">
        <div class="nns-save-button-dropdown-inner" (clickOutside)="onClickOutside($event)">
          <div class="nns-save-button-dropdown-holder">
            <nns-select-dialog
              [profile]="profile"
              [organizations]="organizations"
              [selectedOrganization]="selectedOrganization"
              [workspaces]="workspaces"
              [selectedWorkspace]="selectedWorkspace"
              [state]="state"
              [visual]="visual"
              ></nns-select-dialog>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="!profile">

      <nns-save-button
        class="nns-animated"
        [ngClass]="{ 'primary': isPrimary }"
        [state]="state"
        [showTitle]="showTitle"
        (click)="goToLogin()"
        >
      </nns-save-button>

      <mat-menu #myDrop="matMenu" class="m-auth-drop nns-theme-{{ visual.theme }}">
        <div (click)="$event.preventDefault();$event.stopPropagation()" (keydown)="$event.stopPropagation()">
          <nim-webshare-auth-form
            id="nim-save-action-auth-form"
            (nimClose)="myTrigger.closeMenu()"
            (nimSubmit)="handleAuthSubmit($event)"
            (nimAuthTypeChange)="handleAuthTab($event)"
            [viewType]="1"
            [isShowTabs]="true"
            [isShowClose]="true"
            [authType]="currentAuthType"
            [termsLink]="termsUrl"
            [otpLink]="contactUrl"
            [authUrl]="authUrl"
            >
          </nim-webshare-auth-form>
        </div>
      </mat-menu>

    </div>

  `,
  styles: [`
    :host {
      position: relative;
      display: block;
    }

    .nns-save-button-dropdown {
      position:absolute;
      right:0;
      top:100%;
    }
    .nns-save-button-dropdown-inner {
      padding:12px 0;
      position:relative;
    }

    .nns-save-button-dropdown-holder {
      background: #FFFFFF;
      border: 1px solid #F5F5F5;
      border-radius: 2px;
      box-sizing:border-box;
      width:380px;
      display:block;
      padding:8px 16px;
      box-shadow: 0px 3.2px 7.2px rgba(0, 0, 0, 0.06);
    }

    .nns-button-disabled{
      opacity: 0.6;
      cursor: default;
    }

    @media only screen and (max-width: 768px) {
      .nns-save-button-dropdown-inner {
        width:100%;
        position:fixed;
        left:0;
        top:0;
        height:100%;
        padding:0;
        z-index:170;
        background: rgba(0,0,0,0.54);
        box-sizing:border-box;
      }

      .nns-save-button-dropdown-holder {
        overflow:auto;
        max-height:100%;
        width: 100%;
      }

      .nns-save-button-dropdown-holder /deep/ > * {
        width:60%;
        margin:0 auto;
        background:none;
        border:none;
        box-shadow:none;
      }
    }
  `, `
    :host-context(.nns-theme-night) .nns-save-button-dropdown-holder {
      background: #292E32;
      border: 1px solid #161A1E;
      box-shadow: 4px 4px 38px rgba(19, 30, 32, 0.66)
    }
    @media only screen and (max-width: 768px) {
      :host-context(.nns-theme-night) .nns-save-button-dropdown-holder /deep/ > * {
        width:100%;
        background:none;
        border:none;
        box-shadow:none;
      }
    }

  `]
})
export class SaveActionComponent implements OnDestroy{
  @Input() state;
  @Input() share;
  @Input() note;
  @Input() login;
  @Input() showTitle;
  @Input() shareCopy;
  @Input() visual;
  @Input() isPrimary = false;

  currentAuthType = NIM_AUTH_TYPE.SIGNUP;

  profile$;
  organizations$;
  workspaces$;
  profile;
  termsUrl;
  contactUrl;
  authUrl;

  isSelectReady;
  selectedOrganization;
  selectedWorkspace;
  workspaces;
  organizations;
  subs = new Subscription();

  constructor(
    public _store: Store<IAppState>,
    public _shareCopyService: ShareCopyService,
    private _profileService: ProfileService,
    private _config: Config,
    private _noteService: NoteService,
  ) {
    this.organizations$ = this._store.pipe(select(getAllAvailableOrganizations));
    const selectedOrganization$ = this._store.pipe(select(getSelectedOrganization));
    this.workspaces$ = this._store.pipe(select(getCurrentOrgWorkspaces));
    const selectedWorkspace$ = this._store.pipe(select(getSelectedWorkspace));
    this.profile$ = this._store.pipe(select(selectCurrentProfile));

    this.profile$.subscribe((profile) => {
      this.profile = profile;
    })

    this._store.pipe(select(getIsSelectReady)).subscribe((isSelectReady) => {
      this.isSelectReady = isSelectReady;
    });
    this.termsUrl = this._config.get('NIMBUS_TERMS_URL');
    this.contactUrl = this._config.get('NIMBUS_CONTACT_URL');
    this.authUrl = this._config.get('AUTH_API_URL');

    this.subs.add(
      selectedWorkspace$.subscribe((workspace) => {
        this.selectedWorkspace = workspace;
      })
    )

    this.subs.add(
      selectedOrganization$.subscribe((org) => {
        this.selectedOrganization = org;
      })
    )

    this.subs.add(
      this.organizations$.subscribe((organizations) => {
        this.organizations = organizations;
      })
    )

    this.subs.add(
      this.workspaces$.subscribe((workspaces) => {
        this.workspaces = workspaces;
      })
    )
  }

  onClickOutside($event) {
    const path = $event.composedPath();
    const isSelectDialogClick = path.some((element) => {
      return element.className && element.className.toString().indexOf('nns-save-button-dropdown-holder') !== -1;
    })
    if(this.visual && this.visual.isSelectDialogOpened && !isSelectDialogClick) {
      this._store.dispatch(closeSelectDialog());
    }
  }

  saveToNimbus() {
    if (this.shareCopy.shareCopyStatus === ShareCopyStatus.SHARE_COPY_INITIAL && this.isSelectReady) {
      this._shareCopyService.copy();
    }
  }

  openSelectDialog() {
    if(!this.visual.isSocketConnected) {
      return;
    }

    if (this.shareCopy.shareCopyStatus === ShareCopyStatus.SHARE_COPY_INITIAL && this.isSelectReady) {
      if(this.organizations.length === 1 && this.workspaces.length === 1) {
        this._shareCopyService.copy();
        return;
      }

      if(this.visual && this.visual.isSelectDialogOpened) {
        this._store.dispatch(closeSelectDialog());
        return;
      }
      setTimeout(() => {
        this._store.dispatch(openSelectDialog());
      })
    } else if(this.shareCopy.shareCopyStatus === ShareCopyStatus.SHARE_COPY_DONE) {
      const nodeUrl = this._noteService.getNodeUrl(this.selectedOrganization, this.selectedWorkspace.globalId, this.shareCopy.shareCopyNodeId, this.note.type);
      window.open(nodeUrl, '_blank');
    }
  }

  handleAuthTab(authType: NIM_AUTH_TYPE) {
    console.log('>>>', authType);
    this.currentAuthType = authType;
  }

  focusFormInput() {
    // const input: HTMLInputElement = document.querySelector('#nim-save-action-auth-form input[name="email"]')
    // input.focus();
  }

  goToLogin () {
    let authPath = this._config.get('AUTH_LOGIN_PATH');
    if (authPath.indexOf('?') === -1) {
      authPath = authPath + '?';
    } else {
      authPath = authPath + '&';
    }
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `${ authPath }success=${ currentPath }`
  }

  handleAuthSubmit(formResult) {
    this._profileService.applyAuth(formResult, this.currentAuthType, 'head_right');
    if(this.currentAuthType === NIM_AUTH_TYPE.SIGNUP){
      this._profileService.showSignUpSuccessToast();

    }
    if(this.currentAuthType === NIM_AUTH_TYPE.SIGNUP || this.currentAuthType === NIM_AUTH_TYPE.SIGNIN) {
      this.subs.add(combineLatest([this.profile$, this.workspaces$, this.organizations$])
        .pipe(
          filter(([profile, workspaces, organizations]) => {
            return profile && workspaces && workspaces.length && organizations.length;
          }),
          take(1),
        ).subscribe(([profile, workspaces, organizations]) => {
          console.log('handle auth submit', this.visual)

          setTimeout(() => {
            console.log(this.visual.isSocketConnected)
          }, 100)
          setTimeout(() => {
            console.log(this.visual.isSocketConnected)
          }, 200)
          if(profile && workspaces.length === 1 && organizations.length === 1) {
            this._shareCopyService.copy();
          } else {
            setTimeout(() => {
              this._store.dispatch(openSelectDialog());
            })
          }
      }))
    }

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
